<script setup lang="ts">
import {
  autoUpdate,
  flip,
  offset,
  shift,
  useFloating,
} from '@floating-ui/vue';
import { tpgIconExitToApp, tpgIconSettings } from '@tpg/grammar-web-icons';
import * as proj from 'ol/proj';
import {
  computed,
  onBeforeUnmount,
  onMounted,
  ref,
} from 'vue';

import {
  GrammarCard,
  GrammarMenuItem,
  GrammarSectionHeader,
} from '@nora-monorepo/grammar-ui';
import { useWebViewStore } from '@nora-monorepo/ui-shared';

import { simulatePosition } from '#nora-ui/global/simulate-position';
import { useMapStore } from '#nora-ui/map/stores/map-store';
import { navigateToRoute } from '#nora-ui/router/navigation';
import { RouteName } from '#nora-ui/router/route-names';

interface MapContextMenuProps {
  mapId: string;
}

const props = defineProps<MapContextMenuProps>();

const mapStore = useMapStore();
const webViewStore = useWebViewStore();

const map = mapStore.getMapByIdOrThrow(props.mapId);

const contextMenuRef = ref(null);
const isVisible = ref(false);
const position = ref({ x: 0, y: 0 });
const clickedLocation = ref<{ latitude: number; longitude: number } | null>(null);
const sidc = ref('');

const virtualElement = computed(() => ({
  getBoundingClientRect: () => ({
    x: position.value.x,
    y: position.value.y,
    top: position.value.y,
    left: position.value.x,
    bottom: position.value.y,
    right: position.value.x,
    width: 0,
    height: 0,
  }),
}));

const { floatingStyles } = useFloating(virtualElement, contextMenuRef, {
  whileElementsMounted: autoUpdate,
  placement: 'right-start',
  middleware: [offset(10), flip(), shift({ padding: 5 })],
});

function showMenu(event: MouseEvent) {
  event.preventDefault();
  position.value = { x: event.clientX, y: event.clientY };

  // Convert pixel to geographic coordinates
  const olMap = map.getOlMapOrThrow();
  const pixel = olMap.getEventPixel(event);
  const coordinate = olMap.getCoordinateFromPixel(pixel);
  const [longitude, latitude] = proj.toLonLat(coordinate, map.ref.projection);

  if (latitude === undefined || longitude === undefined) {
    return;
  }

  clickedLocation.value = { latitude, longitude };

  isVisible.value = true;
}

function hideMenu() {
  isVisible.value = false;
}

async function handleSimulatePosition() {
  if (!clickedLocation.value) {
    return;
  }

  await simulatePosition({ ...clickedLocation.value, sidc: sidc.value });
  hideMenu();
}

function openInGoogleMaps() {
  if (!clickedLocation.value) {
    return;
  }

  const googleMapsUrl = `https://www.google.com/maps?q=${clickedLocation.value.latitude},${clickedLocation.value.longitude}`;
  window.open(googleMapsUrl, '_blank');
  hideMenu();
}

function openMapSettings(): void {
  navigateToRoute(RouteName.SettingsMap);
  hideMenu();
}

function handleDocumentClick() {
  hideMenu();
}

onMounted(() => {
  document.addEventListener('click', handleDocumentClick);
  document.addEventListener('contextmenu', handleDocumentClick);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', handleDocumentClick);
  document.removeEventListener('contextmenu', handleDocumentClick);
});

defineExpose({ showMenu, hideMenu });
</script>

<template>
  <div
    ref="contextMenuRef"
    class="z-50"
    :style="floatingStyles"
    @click.stop
  >
    <GrammarCard v-if="isVisible">
      <template #header-stuff>
        <div class="pb-2 pt-2 px-2">
          <div
            v-if="!webViewStore.isWebView"
            class="pb-1"
          >
            <GrammarMenuItem
              label="Open in Google Maps"
              :trailing-icon-template="tpgIconExitToApp"
              @select="openInGoogleMaps"
            />
          </div>
          <div v-if="!webViewStore.isWebView">
            <GrammarMenuItem
              label="Open map settings"
              :trailing-icon-template="tpgIconSettings"
              @select="openMapSettings"
            />
          </div>
          <div
            v-can="{
              permission: 'view:admin.panel',
            }"
          >
            <GrammarSectionHeader label="Debug" />
            <GrammarMenuItem
              label="Simulate position"
              @select="handleSimulatePosition"
            />
          </div>
        </div>
      </template>
    </GrammarCard>
  </div>
</template>
